import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCodeScanner as QrCodeIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  mockAssignments,
  mockMaintenanceAlerts,
  Asset,
  AssetStatus,
  AssetCategory,
  getStatusLabel,
  getStatusColor,
  getCategoryLabel,
} from '../services/mockInventory';
import { api } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';

const Inventory: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin, isAgence } = usePermissions();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serial_number: '',
    qr_code: '',
    category: 'equipment' as AssetCategory,
    status: 'available' as AssetStatus,
    location: '',
    assigned_to: '',
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    maintenance_due_date: '',
    notes: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<AssetCategory | 'all'>('all');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [searchTerm, statusFilter, categoryFilter, assets]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await api.inventory.getAll();
      // Transformer les données Supabase en format Asset
      const transformedAssets: Asset[] = (response.assets || []).map((a: any) => ({
        id: a.id,
        name: a.name,
        description: a.description || '',
        serial_number: a.serial_number || '',
        qr_code: a.qr_code || a.id.substring(0, 8).toUpperCase(),
        category: a.category as AssetCategory,
        status: a.status as AssetStatus,
        location: a.location || '',
        assigned_to: a.assigned_to || '',
        assigned_to_name: a.assigned_to_user?.name || '',
        purchase_date: a.purchase_date || '',
        purchase_price: a.purchase_price ? parseFloat(a.purchase_price) : undefined,
        current_value: a.current_value ? parseFloat(a.current_value) : undefined,
        maintenance_due_date: a.maintenance_due_date || '',
        next_maintenance_date: a.maintenance_due_date || '',
        last_maintenance_date: a.last_maintenance_date || '',
        notes: a.notes || '',
        created_at: a.created_at,
        updated_at: a.updated_at || a.created_at,
      }));
      setAssets(transformedAssets);
    } catch (error) {
      console.error('Erreur lors du chargement des ressources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAssets = () => {
    let filtered = [...assets];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.qr_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter((asset) => asset.status === statusFilter);
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((asset) => asset.category === categoryFilter);
    }

    setFilteredAssets(filtered);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, assetId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(assetId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleViewDetails = (asset: Asset) => {
    setSelectedAsset(asset);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleShowQR = (asset: Asset) => {
    setSelectedAsset(asset);
    setQrDialogOpen(true);
    handleMenuClose();
  };

  const handleScanQR = () => {
    setScannerOpen(true);
  };

  const handleAssignAsset = (asset: Asset) => {
    // Navigation vers une page d'affectation ou ouverture d'un dialog
    navigate(`/inventory/${asset.id}/assign`);
    handleMenuClose();
  };

  const getAlertsCount = () => {
    return mockMaintenanceAlerts.filter((alert) => !alert.resolved_at).length;
  };

  const getAssetsByStatus = (status: AssetStatus) => {
    return assets.filter((asset) => asset.status === status).length;
  };

  const handleAddAsset = async () => {
    try {
      await api.inventory.create({
        name: formData.name,
        description: formData.description || undefined,
        serial_number: formData.serial_number || undefined,
        qr_code: formData.qr_code || undefined,
        category: formData.category,
        status: formData.status,
        location: formData.location || undefined,
        assigned_to: formData.assigned_to || undefined,
        purchase_date: formData.purchase_date || undefined,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : undefined,
        current_value: formData.current_value ? parseFloat(formData.current_value) : undefined,
        maintenance_due_date: formData.maintenance_due_date || undefined,
        notes: formData.notes || undefined,
      });
      setAddDialogOpen(false);
      loadAssets();
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout de la ressource:', error);
      alert('Erreur lors de l\'ajout: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleEditAsset = async () => {
    if (!selectedAsset) return;
    try {
      await api.inventory.update(selectedAsset.id, {
        name: formData.name,
        description: formData.description || undefined,
        serial_number: formData.serial_number || undefined,
        qr_code: formData.qr_code || undefined,
        category: formData.category,
        status: formData.status,
        location: formData.location || undefined,
        assigned_to: formData.assigned_to || undefined,
        purchase_date: formData.purchase_date || undefined,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : undefined,
        current_value: formData.current_value ? parseFloat(formData.current_value) : undefined,
        maintenance_due_date: formData.maintenance_due_date || undefined,
        notes: formData.notes || undefined,
      });
      setEditDialogOpen(false);
      loadAssets();
    } catch (error: any) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette ressource ?')) {
      return;
    }
    try {
      await api.inventory.delete(assetId);
      loadAssets();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleOpenEditDialog = (asset: Asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name,
      description: asset.description || '',
      serial_number: asset.serial_number || '',
      qr_code: asset.qr_code,
      category: asset.category,
      status: asset.status,
      location: asset.location || '',
      assigned_to: asset.assigned_to || '',
      purchase_date: asset.purchase_date || '',
      purchase_price: asset.purchase_price?.toString() || '',
      current_value: asset.current_value?.toString() || '',
      maintenance_due_date: asset.maintenance_due_date || asset.next_maintenance_date || '',
      notes: asset.notes || '',
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Gestion des Ressources et Inventaire
          </Typography>
          <Box>
            {(isAdmin || isAgence) && (
              <Button
                variant="contained"
                startIcon={<QrCodeIcon />}
                onClick={handleScanQR}
                sx={{ mr: 2 }}
              >
                Scanner QR Code
              </Button>
            )}
            {(isAdmin || isAgence) && (
              <Button 
                variant="contained" 
                startIcon={<AssignmentIcon />}
                onClick={() => {
                  setFormData({
                    name: '',
                    description: '',
                    serial_number: '',
                    qr_code: '',
                    category: 'other' as AssetCategory,
                    status: 'available',
                    location: '',
                    assigned_to: '',
                    purchase_date: '',
                    purchase_price: '',
                    current_value: '',
                    maintenance_due_date: '',
                    notes: '',
                  });
                  setAddDialogOpen(true);
                }}
              >
                Nouvelle Ressource
              </Button>
            )}
          </Box>
        </Box>

        {/* Statistiques */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Ressources
                </Typography>
                <Typography variant="h4">{assets.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Stock
                </Typography>
                <Typography variant="h4" color="success.main">
                  {getAssetsByStatus('in_stock')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Déployé
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {getAssetsByStatus('deployed')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Alertes
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {getAlertsCount()}
                    </Typography>
                  </Box>
                  {getAlertsCount() > 0 && (
                    <Badge badgeContent={getAlertsCount()} color="error">
                      <WarningIcon color="warning" />
                    </Badge>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Alertes de maintenance */}
        {getAlertsCount() > 0 && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {getAlertsCount()} alerte(s) de maintenance en attente
            </Typography>
            {mockMaintenanceAlerts
              .filter((alert) => !alert.resolved_at)
              .slice(0, 3)
              .map((alert) => (
                <Typography key={alert.id} variant="body2">
                  • {alert.asset_name}: {alert.message}
                </Typography>
              ))}
          </Alert>
        )}

        {/* Filtres et recherche */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Rechercher par nom, numéro de série ou QR code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Statut"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as AssetStatus | 'all')}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Tous les statuts</option>
                <option value="in_stock">En Stock</option>
                <option value="deployed">Déployé</option>
                <option value="in_repair">En Réparation</option>
                <option value="damaged">Endommagé</option>
                <option value="maintenance_due">Maintenance Due</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Catégorie"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as AssetCategory | 'all')}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Toutes les catégories</option>
                <option value="plv">PLV</option>
                <option value="refrigerator">Réfrigérateur</option>
                <option value="umbrella">Parasol</option>
                <option value="uniform">Uniforme</option>
                <option value="stand">Stand</option>
                <option value="other">Autre</option>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Tableau des ressources */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Numéro de série</TableCell>
                <TableCell>QR Code</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Localisation</TableCell>
                <TableCell>Projet</TableCell>
                <TableCell>Assigné à</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssets.map((asset) => (
                <TableRow key={asset.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{asset.name}</Typography>
                    {asset.description && (
                      <Typography variant="caption" color="textSecondary">
                        {asset.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={getCategoryLabel(asset.category)} size="small" />
                  </TableCell>
                  <TableCell>{asset.serial_number || '-'}</TableCell>
                  <TableCell>
                    <Chip label={asset.qr_code} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(asset.status)}
                      color={getStatusColor(asset.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {asset.location ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{asset.location}</Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {asset.current_project_name ? (
                      <Typography variant="body2">{asset.current_project_name}</Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {asset.assigned_to_name ? (
                      <Typography variant="body2">{asset.assigned_to_name}</Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, asset.id)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Menu contextuel */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedRowId && (
          <>
            <MenuItem
              onClick={() => {
                const asset = assets.find((a) => a.id === selectedRowId);
                if (asset) handleViewDetails(asset);
              }}
            >
              <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
              Voir les détails
            </MenuItem>
            <MenuItem
              onClick={() => {
                const asset = assets.find((a) => a.id === selectedRowId);
                if (asset) handleShowQR(asset);
              }}
            >
              <QrCodeIcon sx={{ mr: 1 }} fontSize="small" />
              Voir QR Code
            </MenuItem>
            {(isAdmin || isAgence) && (
              <>
                <MenuItem
                  onClick={() => {
                    const asset = assets.find((a) => a.id === selectedRowId);
                    if (asset) handleOpenEditDialog(asset);
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize="small" />
                  Modifier
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const asset = assets.find((a) => a.id === selectedRowId);
                    if (asset) handleAssignAsset(asset);
                  }}
                >
                  <AssignmentIcon sx={{ mr: 1 }} fontSize="small" />
                  Affecter
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (selectedRowId) handleDeleteAsset(selectedRowId);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                  Supprimer
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Dialog QR Code */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QR Code - {selectedAsset?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            {selectedAsset && (
              <>
                <QRCodeSVG value={selectedAsset.qr_code} size={256} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {selectedAsset.qr_code}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {selectedAsset.name}
                </Typography>
                {selectedAsset.serial_number && (
                  <Typography variant="body2" color="textSecondary">
                    Série: {selectedAsset.serial_number}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Scanner QR Code */}
      <Dialog open={scannerOpen} onClose={() => setScannerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scanner QR Code</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Fonctionnalité de scan QR Code à implémenter avec une bibliothèque de scan (ex: html5-qrcode)
            </Typography>
            <TextField
              fullWidth
              label="Ou saisir le QR Code manuellement"
              placeholder="QR-XXX-XXX"
              sx={{ mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerOpen(false)}>Annuler</Button>
          <Button variant="contained">Rechercher</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détails */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Détails de la Ressource
          {selectedAsset && (
            <Chip
              label={getStatusLabel(selectedAsset.status)}
              color={getStatusColor(selectedAsset.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedAsset && (
            <Box sx={{ mt: 2 }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Informations" />
                <Tab label="Historique" />
                <Tab label="Maintenance" />
              </Tabs>

              {tabValue === 0 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Nom
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedAsset.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Catégorie
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {getCategoryLabel(selectedAsset.category)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Numéro de série
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedAsset.serial_number || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        QR Code
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedAsset.qr_code}
                      </Typography>
                    </Grid>
                    {selectedAsset.location && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Localisation
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon color="action" />
                          <Typography variant="body1">
                            {selectedAsset.location}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {selectedAsset.current_project_name && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Projet
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedAsset.current_project_name}
                        </Typography>
                      </Grid>
                    )}
                    {selectedAsset.assigned_to_name && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Assigné à
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedAsset.assigned_to_name}
                        </Typography>
                      </Grid>
                    )}
                    {selectedAsset.description && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Description
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedAsset.description}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Historique d'Affectation
                  </Typography>
                  {mockAssignments
                    .filter((assign) => assign.asset_id === selectedAsset.id)
                    .map((assign) => (
                      <Card key={assign.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {assign.project_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Assigné à: {assign.assigned_to_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Localisation: {assign.location}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Date: {new Date(assign.assigned_at).toLocaleDateString('fr-FR')}
                          </Typography>
                          {assign.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Notes: {assign.notes}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  {mockAssignments.filter((assign) => assign.asset_id === selectedAsset.id)
                    .length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      Aucun historique d'affectation
                    </Typography>
                  )}
                </Box>
              )}

              {tabValue === 2 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Maintenance
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedAsset.last_maintenance_date && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Dernière maintenance
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {new Date(selectedAsset.last_maintenance_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      </Grid>
                    )}
                    {selectedAsset.next_maintenance_date && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Prochaine maintenance
                        </Typography>
                        <Typography
                          variant="body1"
                          gutterBottom
                          color={
                            new Date(selectedAsset.next_maintenance_date) < new Date()
                              ? 'error'
                              : 'textPrimary'
                          }
                        >
                          {new Date(selectedAsset.next_maintenance_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  {mockMaintenanceAlerts
                    .filter((alert) => alert.asset_id === selectedAsset.id && !alert.resolved_at)
                    .map((alert) => (
                      <Alert
                        key={alert.id}
                        severity={alert.severity === 'urgent' ? 'error' : 'warning'}
                        sx={{ mt: 2 }}
                      >
                        <Typography variant="subtitle2">{alert.message}</Typography>
                        {alert.due_date && (
                          <Typography variant="body2">
                            Date limite: {new Date(alert.due_date).toLocaleDateString('fr-FR')}
                          </Typography>
                        )}
                      </Alert>
                    ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ajouter/Modifier Ressource */}
      <Dialog 
        open={addDialogOpen || editDialogOpen} 
        onClose={() => {
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {addDialogOpen ? 'Nouvelle Ressource' : 'Modifier la Ressource'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de la ressource"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro de série"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="QR Code"
                value={formData.qr_code}
                onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Catégorie"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as AssetCategory })}
                SelectProps={{ native: true }}
                required
              >
                <option value="equipment">Équipement</option>
                <option value="vehicle">Véhicule</option>
                <option value="material">Matériel</option>
                <option value="tool">Outil</option>
                <option value="other">Autre</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Statut"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as AssetStatus })}
                SelectProps={{ native: true }}
                required
              >
                <option value="available">Disponible</option>
                <option value="assigned">Assigné</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retiré</option>
                <option value="lost">Perdu</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Localisation"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date d'achat"
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prix d'achat"
                type="number"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valeur actuelle"
                type="number"
                value={formData.current_value}
                onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de maintenance due"
                type="date"
                value={formData.maintenance_due_date}
                onChange={(e) => setFormData({ ...formData, maintenance_due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setEditDialogOpen(false);
          }}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={addDialogOpen ? handleAddAsset : handleEditAsset}
            disabled={!formData.name}
          >
            {addDialogOpen ? 'Ajouter' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Inventory;




